// FAME service verticals used on programmatic /services/:service/:city pages.
// These mirror the five pillars already advertised on FAME's /services page:
// AI marketing strategy, Voice AI, SEO automation, ChatGPT ads, and GEO content.
// Every service routes to an existing FAME page so internal linking compounds.

export interface ServiceData {
  slug: string;
  name: string;                 // Title case used in H1/H2
  display_short: string;        // Inline body copy
  tagline: string;              // Plain-English, customer-facing one-liner for the /service-areas directory
  pain_points: [string, string, string];
  avg_deal_value: number;       // annualized retainer anchor used by ROI calc
  avg_deal_value_label: string; // "$60,000"
  common_outcomes: string[];    // 3-5 items
  keyword_primary: string;
  keyword_secondary: string;
  industry_route: string;       // canonical FAME page to link back to
}

export const SERVICES: ServiceData[] = [
  {
    slug: 'ai-marketing',
    name: 'AI Marketing',
    display_short: 'AI marketing',
    tagline:
      'One team running ads, SEO, voice, and content as a single system — with weekly proof of what is actually booking revenue.',
    pain_points: [
      'Your agency retainer delivers decks and dashboards, not leads — and nobody can prove which channel actually booked revenue last quarter.',
      'You\'re paying three vendors for ads, SEO, and reputation management, and none of them share data, so you carry the integration tax.',
      'Generative AI is eating your traffic (ChatGPT, Perplexity, Gemini answer buyers directly) and your current marketing partner has no plan for it.',
    ],
    avg_deal_value: 60000,
    avg_deal_value_label: '$60,000',
    common_outcomes: [
      'One integrated marketing system (ads + SEO + voice + content) instead of four vendors',
      'Measurable revenue attribution to every channel, weekly',
      'AI visibility across ChatGPT, Perplexity, Gemini, and Claude',
      'Predictable lead flow with a CRO-tuned booking engine',
      'Owner dashboard — no more chasing reports',
    ],
    keyword_primary: 'AI marketing agency',
    keyword_secondary: 'AI marketing retainer Florida',
    industry_route: '/services',
  },
  {
    slug: 'voice-ai',
    name: 'Voice AI',
    display_short: 'voice AI',
    tagline:
      'A 24/7 AI receptionist that answers every call, qualifies the lead, and books the job straight into your CRM.',
    pain_points: [
      'Your reception staff covers business hours only, and 40%+ of high-intent calls land after-hours, during lunch, or when the line is busy.',
      'You\'re paying a human answering service $1,500-$3,000/mo that still can\'t book jobs or update your CRM automatically.',
      'Every missed call is a $2,000-$20,000 revenue event walking to the next phone number on the Google results page.',
    ],
    avg_deal_value: 60000,
    avg_deal_value_label: '$60,000',
    common_outcomes: [
      '24/7 voice agent that qualifies, books, and writes to CRM',
      '7-day deployment with your existing phone number',
      'Routing rules for emergencies, VIPs, and repeat clients',
      'Transcripts and recordings piped into your reporting',
      'Measurable lift on booked-call rate inside 30 days',
    ],
    keyword_primary: 'voice AI agency',
    keyword_secondary: 'AI receptionist Florida',
    industry_route: '/voice-ai',
  },
  {
    slug: 'seo-automation',
    name: 'SEO Automation',
    display_short: 'SEO automation',
    tagline:
      'Automated local SEO that puts you in the Google map pack and ranks you for the city-plus-service keywords that drive high-ticket work.',
    pain_points: [
      'Your SEO agency ships reports, not rankings — and your Google Business Profile still hasn\'t been touched since the last algorithm update.',
      'You have 400 reviews and a decent site, but you\'re invisible in the map pack because nobody is managing velocity and schema.',
      'Local competitors two blocks over are outranking you on the exact city + service keywords that drive high-ticket work.',
    ],
    avg_deal_value: 60000,
    avg_deal_value_label: '$60,000',
    common_outcomes: [
      'Weekly rank tracking tied to pipeline, not vanity keywords',
      'Automated schema, citations, and GBP optimization',
      'AI-assisted content engine that ships 8-20 pages a month',
      'Review velocity + reply automation',
      'First-page coverage for your money keywords across Broward',
    ],
    keyword_primary: 'SEO automation agency',
    keyword_secondary: 'local SEO automation Florida',
    industry_route: '/services',
  },
  {
    slug: 'chatgpt-ads',
    name: 'ChatGPT Ads',
    display_short: 'ChatGPT ads',
    tagline:
      'Get your business placed in AI answers and ChatGPT ad slots before your competitors even set up.',
    pain_points: [
      'ChatGPT, Perplexity, and Gemini are sending real referral traffic, and your ad platform of record still only reports on Google and Meta.',
      'You\'re about to be outspent by competitors who set up for conversational ad placements six months before they officially launch.',
      'Nobody on your team owns "AI readiness" — your creative, landing pages, and schema aren\'t built for an assistant to quote.',
    ],
    avg_deal_value: 60000,
    avg_deal_value_label: '$60,000',
    common_outcomes: [
      'ChatGPT-ready landing pages, schema, and answer blocks',
      'Perplexity and Gemini citation optimization',
      'Conversational ad placement setup on day one',
      'Attribution dashboards that include AI-sourced traffic',
      'Early-mover positioning before your competitors even notice',
    ],
    keyword_primary: 'ChatGPT ads agency',
    keyword_secondary: 'generative engine advertising Florida',
    industry_route: '/chatgpt-ads',
  },
  {
    slug: 'geo-content',
    name: 'GEO Content',
    display_short: 'GEO content',
    tagline:
      'Get recommended by name when buyers ask ChatGPT, Perplexity, and Gemini for the best in your city.',
    pain_points: [
      'Your business doesn\'t get recommended when a buyer asks ChatGPT or Perplexity "best [service] in [city]" — and you\'re not sure why.',
      'Your blog posts are optimized for Google 2015, not for the extractive way AI assistants pull answer blocks today.',
      'You\'re doing good work but the knowledge graph doesn\'t know about it — no schema, no entity associations, no citations.',
    ],
    avg_deal_value: 60000,
    avg_deal_value_label: '$60,000',
    common_outcomes: [
      'Generative engine optimization across ChatGPT, Perplexity, Gemini, Claude',
      'Answer-ready content packaged for AI extraction',
      'Entity + knowledge graph positioning',
      'Weekly brand-visibility scans across assistants',
      'Internal linking + citation graph built for AI retrieval',
    ],
    keyword_primary: 'generative engine optimization agency',
    keyword_secondary: 'GEO content Florida',
    industry_route: '/geo',
  },
];

export function getServiceBySlug(slug: string): ServiceData | undefined {
  return SERVICES.find((s) => s.slug === slug);
}

export function getServiceSlugs(): string[] {
  return SERVICES.map((s) => s.slug);
}
